import { useEffect, useState } from 'react';
import { parseToItemsWithKeys } from 'utils/parseToItems';

import useBoundaries from 'hooks/useBoundaries';
import useSelectPanelField from 'hooks/useSelectPanelField';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE, FIELD_BOUNDARIES_PAGE_SIZE } from './constants';
import type {
    BoundaryFieldSchema,
    BoundaryResponse,
    Props,
    UseBoundaryPanelItemsReturn
} from './types';

const parseBoundaries = (boundaries: BoundaryResponse[]): BoundaryFieldSchema[] =>
    parseToItemsWithKeys(boundaries, ['isPrimary', 'siteId', 'siteName']);

const useBoundaryPanelItems = ({
    excludedValues = [],
    siteId
}: Props): UseBoundaryPanelItemsReturn => {
    const {
        fetch: fetchFieldBoundaries,
        isLoading: isFieldBoundariesLoading,
        reset: resetFieldBoundaries
    } = useBoundaries();
    const {
        fetch: fetchAllBoundaries,
        isLoading: isAllBoundariesLoading,
        reset: resetAllBoundaries
    } = useBoundaries();
    const { NO_BOUNDARIES_FOUND } = useTranslation();

    const [fieldItems, setFieldItems] = useState<BoundaryFieldSchema[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    // The API returns primaries first, so a single unscoped page is mostly other fields'
    // primaries. This field's own boundaries are fetched separately so the first group is
    // complete on first paint rather than filling in as the user scrolls.
    const loadFieldItems = async () => {
        if (!siteId) {
            setFieldItems([]);

            return;
        }

        resetFieldBoundaries();

        const generator = fetchFieldBoundaries({
            pageSize: FIELD_BOUNDARIES_PAGE_SIZE,
            siteId
        });

        const { value } = await generator.next();

        setFieldItems(value ? parseBoundaries(value) : []);
    };

    useEffect(() => {
        loadFieldItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId]);

    const {
        handleAsyncFilter: filterAllBoundaries,
        hasNextPage,
        items: allItems,
        noItemsText,
        onNextPage
    } = useSelectPanelField<BoundaryResponse>({
        dependency: siteId,
        fetchFn: args => fetchAllBoundaries({ ...args, includeUnassigned: true }),
        noItemsFoundText: NO_BOUNDARIES_FOUND,
        pageSize: DEFAULT_PAGE_SIZE,
        parseFn: parseBoundaries,
        resetFn: resetAllBoundaries
    });

    const handleAsyncFilter = async (newSearchTerm: string) => {
        setSearchTerm(newSearchTerm);

        await filterAllBoundaries(newSearchTerm);
    };

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    const matchesSearch = ({ label }: BoundaryFieldSchema) =>
        !lowercasedSearchTerm || label.toLowerCase().includes(lowercasedSearchTerm);

    const excludedItemValues = new Set(excludedValues.map(({ value }) => value));
    const visibleFieldItems = fieldItems
        .filter(matchesSearch)
        .filter(({ value }) => !excludedItemValues.has(value));
    const fieldItemValues = new Set(fieldItems.map(({ value }) => value));

    const items: BoundaryFieldSchema[] = [
        ...visibleFieldItems,
        ...allItems.filter(
            ({ value }) => !fieldItemValues.has(value) && !excludedItemValues.has(value)
        )
    ];

    return {
        handleAsyncFilter,
        hasNextPage,
        isLoading: isAllBoundariesLoading || isFieldBoundariesLoading,
        items,
        noItemsText: items.length === 0 ? noItemsText || NO_BOUNDARIES_FOUND : '',
        onNextPage
    };
};

export default useBoundaryPanelItems;
